import React from 'react';
import { useQuery } from 'react-query';
import { evaluateAssetManagement } from 'config/api';
import Loading from 'components/commons/loading/Loading';
import { PremiumCheck2 as PremiumCheck } from 'components/commons/premium-check/PremiumCheck';
import {
  TargetAccountItem,
  AccountId,
  howToSaveCash,
  howToSaveInvestmentProducts,
  Target,
} from 'components/features/target-achievement-support/utils/type';
import { useBirthday } from 'components/features/target-achievement-support/utils/birthday';
import { useTargetSupportRouting } from 'components/features/target-achievement-support/hooks/useTargetSupportRouting';
import { yieldFormatPercentage } from 'components/features/new-report/utils/formatter';
import { EvaluateAssetManagementProps } from './ChangeYieldCalculationForm';
import { SettingTargetChart } from '../detail/components/SettingTargetChart';
import { isHowToSaveInvestmentTrust } from '../detail/TargetImformation';
import { NotFoundBirthday } from '../birthday/NotFoundBirthday';
import { TargetPremium } from '../TargetPremium';

const HowToSaveInformationCard = ({
  evaluateAssetManagement,
  className,
}: {
  evaluateAssetManagement: EvaluateAssetManagementProps | null;
  className?: string;
}): React.ReactElement => {
  const getHighRiskMessage = (
    annualYield: number,
    suggestionLimitPer: number,
    highRiskPer: number
  ): React.ReactElement => {
    if (annualYield < suggestionLimitPer / 100) return <></>;
    if (annualYield < highRiskPer / 100)
      return (
        <>
          「目的達成に必要な年利回り」が少し高めです。リスクを分散した運用の提案ができない場合があります
        </>
      );
    return (
      <>
        「目的達成に必要な年利回り」が高いです。
        <br />
        高いリターンが期待できる一方、リスクも高く、状況によっては達成できない可能性が大きくなります。また、リスク分散のための提案ができない場合があります。
      </>
    );
  };

  return (
    <>
      {evaluateAssetManagement &&
        ((): React.ReactElement => {
          const suggestionLimitPer =
            evaluateAssetManagement.suggestionLimitPer ?? 0;
          const highRiskPer = evaluateAssetManagement.highRiskPer ?? 0;
          const type =
            evaluateAssetManagement.yield === 0 ||
            evaluateAssetManagement.yield === null
              ? howToSaveCash
              : howToSaveInvestmentProducts;

          if (isHowToSaveInvestmentTrust(type)) {
            return (
              <>
                <div className={` ${className ?? ''}`}>
                  <div className="rounded-lg bg-teal-500 text-white">
                    <div className="border-b border-white px-4 pt-4 pb-2 flex flex-row items-center">
                      <img
                        src="/static/images/advisor/investment1.svg"
                        alt="パソコン画像"
                        className="h-16 w-16 rounded-full"
                        style={{ objectFit: 'cover' }}
                      />
                      <div className="flex flex-col items-start ml-6">
                        <div className="text-sm">貯め方</div>
                        <div className="mt-1 text-lg font-bold">
                          投資商品で運用
                        </div>
                      </div>
                    </div>
                    <div className="px-4 py-2 flex flex-row items-center">
                      <div className="text-sm">目標達成に必要な年利回り</div>
                      <div className="font-bold text-lg ml-auto">
                        {yieldFormatPercentage(evaluateAssetManagement.yield, {
                          numberOfDecimals: 1,
                        })}
                        %
                      </div>
                    </div>
                  </div>
                  {evaluateAssetManagement.yield >=
                    suggestionLimitPer / 100 && (
                    <div className="flex flex-row items-start text-tradewell mx-2 mt-3">
                      <div className="icon-alert"></div>
                      <div className="text-sm ml-2">
                        {getHighRiskMessage(
                          evaluateAssetManagement.yield,
                          suggestionLimitPer,
                          highRiskPer
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            );
          } else {
            return (
              <>
                <div
                  className={`rounded-lg bg-blue-450 text-white flex flex-row px-4 py-4 ${className ??
                    ''}`}>
                  <img
                    src="/static/images/advisor/cash1.svg"
                    className="h-16 w-16 rounded-full"
                    alt="現金画像"
                    style={{ objectFit: 'cover' }}
                  />
                  <div className="ml-6 flex flex-col items-start">
                    <div className="text-sm">貯め方</div>
                    <div className="mt-1 text-lg font-bold">現金でつみたて</div>
                  </div>
                </div>
              </>
            );
          }
        })()}
    </>
  );
};

export const CheckYieldCalculationForm = ({
  id,
  target,
  selectedAccounts,
  excludeAmount,
}: {
  id: string;
  target: Target;
  selectedAccounts: TargetAccountItem[];
  excludeAmount: number;
}) => {
  const { goWhatIsNecessaryYield } = useTargetSupportRouting();

  const selectedAccountIds: AccountId[] =
    selectedAccounts.length !== 0
      ? selectedAccounts.map(list => list.account)
      : [];

  const { data: evaluateAssetManagement, isLoading, isRefetching } = useQuery(
    'targetHowToSavetestEvaluateAssetManagement',
    () =>
      evaluateAssetManagementAPI(
        target.id,
        selectedAccountIds,
        excludeAmount,
        target.reserveAmount,
        target.targetAmount,
        target.endDate
      ),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { birthday } = useBirthday();

  if (birthday === null || birthday === undefined)
    return (
      <>
        <PremiumCheck content={<TargetPremium />}>
          <NotFoundBirthday />
        </PremiumCheck>
      </>
    );

  if (
    evaluateAssetManagement === null ||
    evaluateAssetManagement === undefined ||
    isLoading ||
    isRefetching
  )
    return (
      <>
        <div className="font-bold mt-4 text-center">シミュレーション結果</div>
        <div className="py-8 bg-white flex flex-col">
          <Loading loading circularLoading />
        </div>
      </>
    );

  return (
    <>
      <div className="font-bold mt-4 text-center">シミュレーション結果</div>
      <div className="pt-2 bg-white flex flex-col">
        <SettingTargetChart
          birthday={birthday}
          targetAmount={target.targetAmount}
          assetTransitions={evaluateAssetManagement.assetTransitions}
        />
        <HowToSaveInformationCard
          {...{
            evaluateAssetManagement,
          }}
          className="mt-4"
        />
        <div
          className="flex flex-row ml-auto items-center mt-2 cursor-pointer"
          onClick={() => goWhatIsNecessaryYield(id)}>
          <div className="text-sm text-bluegray-500">必要な年利回りとは</div>
          <div className="icon-help_outline text-base text-bluegray-400 ml-2"></div>
        </div>
      </div>
    </>
  );
};

const evaluateAssetManagementAPI = async (
  id: string,
  accountIDList: string[],
  excludeAmount: number | null,
  reserveAmount: number | null,
  targetAmount: number,
  endDate: string
): Promise<EvaluateAssetManagementProps | null> => {
  if (reserveAmount === null || reserveAmount < 0) return null;

  const validExcludeAmount = excludeAmount ?? 0;
  const res_ = await evaluateAssetManagement(
    id,
    accountIDList,
    validExcludeAmount,
    reserveAmount,
    targetAmount,
    endDate
  );

  if (res_ === null) return null;
  const res = {
    yield: res_.Yield !== null ? res_.Yield : 0,
    suggestionLimitPer: res_.SuggestionLimitPer,
    highRiskPer: res_.HighRiskPer,
    assetTransitions: res_.AssetTransitions.map(item => {
      return {
        dateYM: item.DateYM,
        assetAmount: item.AssetAmount,
      };
    }),
  };

  return res as EvaluateAssetManagementProps;
};
